import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import PageCard from '../../components/ui/PageCard';
import MagazinePageHeader from '../magazine/MagazinePageHeader';
import { useAuth } from '../../hooks/useAuth';
import { Contract, deleteContract, getContracts } from '../../api/contractService';

const statusClass: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-50 text-blue-700',
  signed: 'bg-green-50 text-green-700',
};

const sourceSummary = (contract: Contract) => {
  if (contract.proposal_id && contract.proposal?.line_items?.length) {
    const count = contract.proposal.line_items.length;
    const first = contract.proposal.line_items[0];
    const pub = first.ad_slot?.issue?.publication?.name;
    return `Proposal #${contract.proposal_id}${pub ? ` · ${pub}` : ''} · ${count} item${count === 1 ? '' : 's'}`;
  }
  const slot = contract.magazine_booking?.ad_slot;
  const parts = [
    slot?.issue?.publication?.name,
    slot?.issue?.title,
    slot?.label,
  ].filter(Boolean);
  return parts.join(' · ') || '—';
};

const Contracts = () => {
  const { isAdmin, canEditAnyDeal } = useAuth();
  const [items, setItems] = useState<Contract[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const data = await getContracts(statusFilter.length > 0 ? [statusFilter] : ['sent', 'signed']);
    setItems(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [statusFilter]);

  const handleDelete = async (id: number) => {
    if (!isAdmin || !window.confirm('Delete this contract?')) return;
    await deleteContract(id);
    load();
  };

  return (
    <Layout>
      <MagazinePageHeader
        title="Contracts"
        description="Magazine advertising contracts generated from proposals."
      />

      <PageCard>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <label className="text-sm text-gray-600">
            Status
            <select
              className="ml-2 rounded border border-gray-300 px-2 py-1 text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="sent">Sent</option>
              <option value="signed">Signed</option>
            </select>
          </label>
        </div>

        {loading ? (
          <p className="text-sm text-gray-500">Loading...</p>
        ) : items.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">No contracts yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-gray-500">
                  <th className="py-2 pr-4">Company</th>
                  <th className="py-2 pr-4">Source</th>
                  <th className="py-2 pr-4">Template</th>
                  {canEditAnyDeal && <th className="py-2 pr-4">Sales rep</th>}
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Created</th>
                  <th className="py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-b border-gray-100">
                    <td className="py-3 pr-4 font-medium text-gray-900">
                      {item.company?.name || '—'}
                    </td>
                    <td className="py-3 pr-4 text-gray-600">{sourceSummary(item)}</td>
                    <td className="py-3 pr-4 text-gray-600">{item.template?.name || '—'}</td>
                    {canEditAnyDeal && (
                      <td className="py-3 pr-4 text-gray-600">
                        {item.sales_rep?.username || item.sales_rep?.email || '—'}
                      </td>
                    )}
                    <td className="py-3 pr-4">
                      <span className={`rounded px-2 py-0.5 text-xs font-medium capitalize ${statusClass[item.status] || ''}`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-gray-600">
                      {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '—'}
                    </td>
                    <td className="py-3">
                      <div className="flex gap-2">
                        <Link to={`/contracts/${item.id}`} className="text-sm text-torch-primary hover:underline">
                          View
                        </Link>
                        {isAdmin && (
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="text-sm text-red-600 hover:underline"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PageCard>
    </Layout>
  );
};

export default Contracts;
